import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Skeleton } from '~components/Skeleton/Skeleton';
import type { MessagingTyper } from '~hooks/useMessaging/models';
import {
  isOwnMessage,
  MessagingChannel,
  MessagingMessage,
} from '~hooks/useMessaging/models';
import useOlderPageSentinel from '~hooks/useMessaging/useOlderPageSentinel';
import MessageRow from '~pages/Messages/components/MessageRow';
import MessagingLoadError from '~pages/Messages/components/MessagingLoadError';
import MessagingTypingIndicator from '~pages/Messages/components/MessagingTypingIndicator';
import OutboxMessageRow from '~pages/Messages/components/OutboxMessageRow';
import { MessagingOutboxEntry } from '~pages/Messages/MessagingOutboxContext';
import { MESSAGING_LABEL_FONT_WEIGHT } from '~pages/Messages/messagingTypography';
import { transcriptItems } from '~pages/Messages/transcriptItems';
import { Nullable } from '~types/Nullable';

// About one bubble's height, so a fractional scrollTop still counts as anchored.
const BOTTOM_ANCHOR_TOLERANCE_IN_PX = 48;

const SKELETON_BUBBLES = [
  { height: 40, isOwn: false, width: '45%' },
  { height: 56, isOwn: false, width: '60%' },
  { height: 40, isOwn: true, width: '40%' },
  { height: 40, isOwn: false, width: '35%' },
  { height: 72, isOwn: true, width: '55%' },
  { height: 40, isOwn: false, width: '50%' },
];

// Fixed so the loading and error rows swap in without shifting the messages below them.
const OLDER_PAGE_SLOT_HEIGHT_IN_PX = 40;

// Keeps the divider off the pane's top edge so it reads as a boundary, not a clipped row.
const UNREAD_DIVIDER_TOP_GAP_IN_PX = 16;

const countArrivals = (
  messages: MessagingMessage[],
  previousLastMessage: Nullable<MessagingMessage>,
  viewerUserId: Nullable<string>,
) => {
  const previousLastIndex = messages.findIndex(
    (message) => message.id === previousLastMessage?.id,
  );
  // A refetch can drop the newest message the reader saw, so its timestamp marks where arrivals start.
  const arrivals =
    previousLastIndex === -1
      ? messages.filter((message) =>
          previousLastMessage
            ? message.createdAt.isAfter(previousLastMessage.createdAt)
            : true,
        )
      : messages.slice(previousLastIndex + 1);

  return arrivals.filter((message) => !isOwnMessage(message, viewerUserId)).length;
};

type MessagingTranscriptProps = {
  canModerate: boolean;
  channel: Nullable<MessagingChannel>;
  hasLoadFailed: boolean;
  hasOlder: boolean;
  hasOlderLoadFailed: boolean;
  isLoading: boolean;
  isLoadingOlder: boolean;
  messages: MessagingMessage[];
  onLoadOlder: () => void;
  onRetryLoad: () => void;
  onRetrySend: (clientMessageId: string) => void;
  outboxEntries: MessagingOutboxEntry[];
  typers: MessagingTyper[];
  unreadDividerMessageId: Nullable<string>;
  viewerUserId: Nullable<string>;
};

const dayLabel = (day: Dayjs) => {
  const today = dayjs();

  if (day.isSame(today, 'day')) {
    return t('messaging.today');
  }

  if (day.isSame(today.subtract(1, 'day'), 'day')) {
    return t('messaging.yesterday');
  }

  return day.format('MMM D, YYYY');
};

export default function MessagingTranscript({
  canModerate,
  channel,
  hasLoadFailed,
  hasOlder,
  hasOlderLoadFailed,
  isLoading,
  isLoadingOlder,
  messages,
  onLoadOlder,
  onRetryLoad,
  onRetrySend,
  outboxEntries,
  typers,
  unreadDividerMessageId,
  viewerUserId,
}: MessagingTranscriptProps) {
  const theme = useTheme();

  const scrollRef = useRef<HTMLDivElement>(null);
  const unreadDividerRef = useRef<HTMLDivElement>(null);
  const isAnchoredToBottomRef = useRef(true);
  const previousScrollHeightRef = useRef(0);
  const previousFirstMessageIdRef = useRef<Nullable<string>>(null);
  const previousLastMessageRef = useRef<Nullable<MessagingMessage>>(null);
  const [unseenMessageCount, setUnseenMessageCount] = useState(0);
  const [isMoreThanAScreenFromBottom, setIsMoreThanAScreenFromBottom] = useState(false);

  const { rearm: rearmOlderPageFetch, sentinelRef: olderPageSentinelRef } =
    useOlderPageSentinel<HTMLDivElement>({
      hasOlder,
      hasOlderLoadFailed,
      isLoadingOlder,
      onLoadOlder,
      scrollRef,
    });

  const scrollToNewest = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    scroller.scrollTop = scroller.scrollHeight;
    isAnchoredToBottomRef.current = true;
    setIsMoreThanAScreenFromBottom(false);
    setUnseenMessageCount(0);
  }, []);

  const syncScrollPosition = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const distanceFromBottom =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    isAnchoredToBottomRef.current = distanceFromBottom <= BOTTOM_ANCHOR_TOLERANCE_IN_PX;
    setIsMoreThanAScreenFromBottom(distanceFromBottom > scroller.clientHeight);

    if (isAnchoredToBottomRef.current) {
      setUnseenMessageCount(0);
    }
  }, []);

  const onScroll = useCallback(() => {
    rearmOlderPageFetch();
    syncScrollPosition();
  }, [rearmOlderPageFetch, syncScrollPosition]);

  // A programmatic scrollTop fires no scroll event in time for the effects that read the anchor.
  const scrollToUnreadDivider = useCallback(() => {
    const scroller = scrollRef.current;
    const divider = unreadDividerRef.current;
    if (!scroller || !divider) return;

    const dividerTop =
      divider.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
    scroller.scrollTop = Math.max(
      0,
      Math.min(dividerTop - UNREAD_DIVIDER_TOP_GAP_IN_PX, maxScrollTop),
    );
    syncScrollPosition();
  }, [syncScrollPosition]);

  const firstMessageId = messages[0]?.id;
  const lastMessage = messages.at(-1) ?? null;

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const isFirstPaint =
      !previousFirstMessageIdRef.current && !previousLastMessageRef.current;
    const isOlderPagePrepended =
      !isFirstPaint && firstMessageId !== previousFirstMessageIdRef.current;
    const hasNewestChanged = lastMessage?.id !== previousLastMessageRef.current?.id;
    const isOwnNewest = lastMessage !== null && isOwnMessage(lastMessage, viewerUserId);

    if (isOlderPagePrepended) {
      scroller.scrollTop += scroller.scrollHeight - previousScrollHeightRef.current;
    } else if (
      isFirstPaint ||
      isAnchoredToBottomRef.current ||
      (hasNewestChanged && isOwnNewest)
    ) {
      scrollToNewest();
    } else if (hasNewestChanged) {
      const arrivals = countArrivals(
        messages,
        previousLastMessageRef.current,
        viewerUserId,
      );
      setUnseenMessageCount((count) => count + arrivals);
    }

    previousFirstMessageIdRef.current = firstMessageId;
    previousLastMessageRef.current = lastMessage;
    previousScrollHeightRef.current = scroller.scrollHeight;
  }, [firstMessageId, lastMessage, messages, scrollToNewest, viewerUserId]);

  // Declared after the effect above so a page prepended in the same commit is adjusted first.
  useLayoutEffect(() => {
    if (unreadDividerMessageId) {
      scrollToUnreadDivider();
    }
  }, [scrollToUnreadDivider, unreadDividerMessageId]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    // Nothing above re-pins on a viewport change, and the pane shrinks after first paint —
    // the composer mounts once the channel loads, the agent dock opens, the window resizes —
    // which would leave a reader who was at the newest message stranded above the fold.
    const observer = new ResizeObserver(() => {
      if (isAnchoredToBottomRef.current) {
        scrollToNewest();
      }
    });
    observer.observe(scroller);

    return () => observer.disconnect();
  }, [scrollToNewest]);

  const items = transcriptItems(messages, viewerUserId, unreadDividerMessageId);

  // An entry whose message already reached the transcript would render twice.
  const sentClientMessageIds = new Set(
    messages.map((message) => message.clientMessageId).filter(Boolean),
  );
  const visibleOutboxEntries = outboxEntries.filter(
    (entry) => !sentClientMessageIds.has(entry.clientMessageId),
  );

  const isEmpty =
    !isLoading &&
    !hasLoadFailed &&
    messages.length === 0 &&
    visibleOutboxEntries.length === 0;

  const visibleOutboxCountRef = useRef(visibleOutboxEntries.length);

  useLayoutEffect(() => {
    const hasNewOutboxEntry = visibleOutboxEntries.length > visibleOutboxCountRef.current;
    visibleOutboxCountRef.current = visibleOutboxEntries.length;

    if (hasNewOutboxEntry) {
      scrollToNewest();
    }
  }, [scrollToNewest, visibleOutboxEntries.length]);

  const hasTypers = typers.length > 0;
  const hasUnseenMessages = unseenMessageCount > 0;
  const shouldOfferJumpToLatest = hasUnseenMessages || isMoreThanAScreenFromBottom;

  useLayoutEffect(() => {
    if (hasTypers && isAnchoredToBottomRef.current) {
      scrollToNewest();
    }
  }, [hasTypers, scrollToNewest]);

  return (
    <Box
      sx={{
        backgroundColor: theme.semantic.surfaceSunken,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        position: 'relative',
      }}
    >
      <Box
        data-test-id="messaging-transcript-scroller"
        onScroll={onScroll}
        ref={scrollRef}
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 0.5,
          // The browser's own anchoring would shift a prepended page a second time.
          overflowAnchor: 'none',
          overflowY: 'auto',
          px: 3,
          py: 2,
        }}
      >
        {isLoading && <MessagingTranscriptSkeleton />}

        {hasLoadFailed && (
          <Box sx={{ py: 4 }}>
            <MessagingLoadError
              message={t('messaging.load_messages_failed')}
              onRetry={onRetryLoad}
            />
          </Box>
        )}

        {isEmpty && (
          <Typography
            sx={{ color: theme.semantic.textSecondary, py: 4, textAlign: 'center' }}
          >
            {t('messaging.no_messages')}
          </Typography>
        )}

        {hasOlder && (
          <Box
            data-test-id="messaging-older-page-sentinel"
            ref={olderPageSentinelRef}
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexShrink: 0,
              justifyContent: 'center',
              minHeight: OLDER_PAGE_SLOT_HEIGHT_IN_PX,
            }}
          >
            {isLoadingOlder && <CircularProgress size={20} />}

            {!isLoadingOlder && hasOlderLoadFailed && (
              <MessagingLoadError
                message={t('messaging.load_older_failed')}
                noWrap
                onRetry={onLoadOlder}
              />
            )}
          </Box>
        )}

        {items.map((item) => {
          switch (item.kind) {
            case 'unread-divider':
              return (
                <Box
                  data-test-id="messaging-unread-divider"
                  key={item.key}
                  ref={unreadDividerRef}
                  sx={{ alignItems: 'center', display: 'flex', gap: 1.5, py: 1 }}
                >
                  <Box
                    sx={{ borderTop: `1px solid ${theme.semantic.linkText}`, flex: 1 }}
                  />
                  <Typography
                    sx={{
                      color: theme.semantic.linkText,
                      fontWeight: MESSAGING_LABEL_FONT_WEIGHT,
                    }}
                    variant="caption"
                  >
                    {t('messaging.new_messages')}
                  </Typography>
                  <Box
                    sx={{ borderTop: `1px solid ${theme.semantic.linkText}`, flex: 1 }}
                  />
                </Box>
              );
            case 'day-separator':
              return (
                <Box
                  key={item.key}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1.5,
                    py: 1.5,
                    '&::before, &::after': {
                      borderTop: `1px solid ${theme.semantic.borderStrong}`,
                      content: '""',
                      flex: 1,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: theme.semantic.textSecondary,
                      fontWeight: MESSAGING_LABEL_FONT_WEIGHT,
                    }}
                    variant="caption"
                  >
                    {dayLabel(item.day)}
                  </Typography>
                </Box>
              );
            case 'message':
              return (
                <MessageRow
                  canModerate={canModerate}
                  channel={channel}
                  endsRun={item.endsRun}
                  isOwn={item.isOwn}
                  key={item.key}
                  message={item.message}
                  showSender={item.showSender}
                  startsRun={item.startsRun}
                  viewerUserId={viewerUserId}
                />
              );
            default:
              throw new Error('Unsupported transcript item');
          }
        })}

        {visibleOutboxEntries.map((entry) => (
          <OutboxMessageRow
            entry={entry}
            key={entry.clientMessageId}
            onRetry={onRetrySend}
          />
        ))}

        <MessagingTypingIndicator typers={typers} />
      </Box>

      {shouldOfferJumpToLatest && (
        <Chip
          clickable
          icon={<KeyboardArrowDownIcon />}
          label={
            hasUnseenMessages
              ? t('messaging.new_messages_count', { count: unseenMessageCount })
              : t('messaging.jump_to_latest')
          }
          onClick={scrollToNewest}
          sx={{
            backgroundColor: theme.semantic.surfaceOverlay,
            border: `1px solid ${theme.semantic.border}`,
            bottom: theme.spacing(2),
            left: '50%',
            position: 'absolute',
            transform: 'translateX(-50%)',
          }}
        />
      )}
    </Box>
  );
}

function MessagingTranscriptSkeleton() {
  return (
    <Box
      data-test-id="messaging-transcript-skeleton"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}
    >
      {SKELETON_BUBBLES.map((bubble, index) => (
        <Skeleton
          height={bubble.height}
          key={index}
          sx={{ alignSelf: bubble.isOwn ? 'flex-end' : 'flex-start' }}
          variant="rectangular"
          width={bubble.width}
        />
      ))}
    </Box>
  );
}
